const LABEL_FRACTIONS = [
  0.5,
  0.45,
  0.55,
  0.4,
  0.6,
  0.35,
  0.65,
  0.3,
  0.7,
  0.25,
  0.75,
  0.2,
  0.8,
  0.15,
  0.85,
  0.1,
  0.9
]

const distanceToSegment = (point, from, to) => {
  const dx = to.x - from.x
  const dy = to.y - from.y
  const lengthSquared = dx * dx + dy * dy
  const fraction = lengthSquared
    ? Math.max(
        0,
        Math.min(
          1,
          ((point.x - from.x) * dx + (point.y - from.y) * dy) / lengthSquared
        )
      )
    : 0
  return Math.hypot(
    point.x - from.x - fraction * dx,
    point.y - from.y - fraction * dy
  )
}

const getPointAlongLine = (points, lengths, distance) => {
  let remaining = distance
  for (let index = 1; index < points.length; index += 1) {
    const length = lengths[index - 1]
    if (remaining <= length || index === points.length - 1) {
      const fraction = length ? Math.min(1, remaining / length) : 0
      return {
        x:
          points[index - 1].x +
          fraction * (points[index].x - points[index - 1].x),
        y:
          points[index - 1].y +
          fraction * (points[index].y - points[index - 1].y)
      }
    }
    remaining -= length
  }
  return points[0]
}

// Anchor badges on the rendered lines, preferring distinct branches and
// separate positions along shared sections instead of offsets beside the road.
export const getRouteDistanceLabelPositions = (routes, map, padding = 0) => {
  const inset =
    typeof padding === 'number'
      ? { top: padding, right: padding, bottom: padding, left: padding }
      : padding
  const canvas = map.getCanvas()
  const projectedRoutes = routes.map(route =>
    route.coordinates.map(coordinate => map.project(coordinate))
  )
  const placed = []
  return projectedRoutes.map((points, routeIndex) => {
    const lengths = points
      .slice(1)
      .map((point, index) =>
        Math.hypot(point.x - points[index].x, point.y - points[index].y)
      )
    const totalLength = lengths.reduce((sum, length) => sum + length, 0)
    const candidates = LABEL_FRACTIONS.map(fraction => {
      const point = getPointAlongLine(points, lengths, totalLength * fraction)
      const isVisible =
        point.x >= (inset.left || 0) + 40 &&
        point.x <= canvas.clientWidth - (inset.right || 0) - 40 &&
        point.y >= (inset.top || 0) + 20 &&
        point.y <= canvas.clientHeight - (inset.bottom || 0) - 20
      const overlaps = placed.some(
        other =>
          Math.abs(point.x - other.x) < 90 && Math.abs(point.y - other.y) < 42
      )
      let clearance = 40
      projectedRoutes.forEach((otherPoints, otherIndex) => {
        if (otherIndex === routeIndex) return
        for (let index = 1; index < otherPoints.length; index += 1) {
          clearance = Math.min(
            clearance,
            distanceToSegment(point, otherPoints[index - 1], otherPoints[index])
          )
        }
      })
      return {
        point,
        score:
          (isVisible ? 10000 : 0) -
          (overlaps ? 1000 : 0) +
          clearance * 2 -
          Math.abs(fraction - 0.5) * 40
      }
    })
    const best = candidates.reduce((current, candidate) =>
      candidate.score > current.score ? candidate : current
    )
    placed.push(best.point)
    return map.unproject(best.point)
  })
}

export const createRouteDistanceLabels = ({
  map,
  Marker,
  routes,
  color,
  onSelect,
  getPadding
}) => {
  const labels = routes.reduce((result, route, routeIndex) => {
    if (
      !Number.isFinite(route.distanceKm) ||
      route.distanceKm < 0 ||
      !route.coordinates ||
      route.coordinates.length < 2
    ) {
      return result
    }
    const element = document.createElement('button')
    element.type = 'button'
    element.className = 'exchange-route-distance'
    element.textContent = `${Math.round(route.distanceKm)} km`
    element.style.setProperty('--route-color', color)
    element.setAttribute('aria-pressed', 'false')
    element.addEventListener('click', event => {
      event.stopPropagation()
      onSelect(routeIndex)
    })
    const marker = new Marker({
      element,
      anchor: 'center',
      rotationAlignment: 'viewport',
      pitchAlignment: 'viewport'
    })
      .setLngLat(route.coordinates[Math.floor(route.coordinates.length / 2)])
      .addTo(map)
    // MapLibre assigns its generic marker title in addTo().
    element.setAttribute('aria-label', element.textContent)
    result.push({ element, marker, route, routeIndex })
    return result
  }, [])
  const reposition = () => {
    const positions = getRouteDistanceLabelPositions(
      labels.map(label => label.route),
      map,
      getPadding ? getPadding() : 0
    )
    labels.forEach((label, index) => label.marker.setLngLat(positions[index]))
  }
  map.on('moveend', reposition)
  map.on('resize', reposition)
  reposition()
  let removed = false
  return {
    setSelectedRouteIndex: selectedIndex => {
      labels.forEach(({ element, routeIndex }) =>
        element.setAttribute(
          'aria-pressed',
          String(routeIndex === selectedIndex)
        )
      )
    },
    remove: () => {
      if (removed) return
      removed = true
      map.off('moveend', reposition)
      map.off('resize', reposition)
      labels.forEach(({ marker }) => marker.remove())
    }
  }
}

export const ROUTE_DISTANCE_LABEL_STYLES = `
  .exchange-route-distance {
    min-width: 58px;
    padding: 3px 9px;
    border: 1px solid #78858d;
    border-radius: 14px;
    background: #fff;
    color: #36434b;
    box-shadow: 0 1px 3px rgba(24, 44, 54, 0.16);
    font-family: 'Open Sans', sans-serif;
    font-size: 13px;
    font-weight: 600;
    line-height: 17px;
    white-space: nowrap;
    cursor: pointer;
    transition: border-color 0.15s ease, background-color 0.15s ease;
  }
  .exchange-route-distance[aria-pressed='true'] {
    border-color: var(--route-color);
    background: var(--route-color);
    color: #fff;
  }
  .exchange-route-distance:hover {
    border-color: var(--route-color);
    background: color-mix(in srgb, var(--route-color) 6%, #fff);
  }
  .exchange-route-distance[aria-pressed='true']:hover {
    background: var(--route-color);
  }
  .exchange-route-distance:focus-visible {
    outline: 3px solid var(--route-color);
    outline-offset: 3px;
  }
`
